// src/ui/ReelMeta.jsx — the strip under a reel's author line: the safety
// notice, the caption, and the hashtags.
//
// 🟢 THE BROWSER'S HALF OF app/src/components/ReelMeta.tsx. Same order, same
//    rules, same services/videoTags underneath — notice, name, caption, tags,
//    track, numbers. gtest asserts the order against both files, because "the
//    same feature on two clients" is mostly a claim about order.
//
// 🟥 THE TWO CLAMPS ARE NOT THE SAME MECHANISM AND CANNOT BE. React Native
//    reports the lines it drew; a browser reports how tall the text wanted to
//    be versus how tall the box is. Both answer "is any of it hidden", each in
//    the only way its platform can, and both are pure functions so the question
//    is tested rather than eyeballed.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hashtagSegments, safetyKeys } from '../services/videoTags';

/* ── the wording ──────────────────────────────────────────────────────────
   🟥 ENGLISH ONLY, AND DELIBERATELY SO. The phone is translated into ten
      languages and resolves these through i18n; the browser has no translation
      layer at all — every string in this client is written in English in place.
      Putting the same sentences here, keyed identically, is what lets gtest
      check that the two clients say the same thing: the app's en.json entry for
      `vibe_safety_fire` and this table's `fire` must match word for word. */
export const SAFETY_TEXT = {
  general: 'The actions in this video could be dangerous. Do not attempt them.',
  fire: 'This video involves fire. It can cause serious burns — do not attempt it.',
  animals: 'This video involves wild animals, handled by people trained to do it. Do not attempt it.',
  heights: 'This video involves heights or stunts performed by trained people. Do not attempt it.',
  weapons: 'This video involves blades or weapons. It can cause serious injury — do not attempt it.',
  chemicals: 'This video involves chemicals or electricity. It can cause serious injury — do not attempt it.',
};
export const SAFETY_TITLE = 'Safety warning';
export const SAFETY_PILL = 'Safety';
export const SAFETY_CLOSE = 'Got it';

/* ── the caption ─────────────────────────────────────────────────────────── */

/** 🟥 Two lines, not three — see the note in the phone's twin. */
export const CAPTION_LINES = 2;

/**
 * Is any of the caption hidden by the clamp?
 *
 * 🟢 A PIXEL OF SLACK. Sub-pixel line heights mean a box that fits exactly can
 *    still report a scrollHeight a fraction larger than its clientHeight, which
 *    would put a "more" under every one-line caption in the feed.
 */
export function showCaptionToggle(scrollH, clientH) {
  return Number(scrollH) > Number(clientH) + 1;
}

export function ReelCaption({ text, onTag }) {
  const [open, setOpen] = useState(false);
  const [clipped, setClipped] = useState(false);
  const ref = useRef(null);
  const src = String(text || '');
  const segments = useMemo(() => hashtagSegments(src), [src]);

  /* 🟥 MEASURED AFTER EVERY LAYOUT THAT COULD CHANGE THE ANSWER. The reel pane
     is drag-resizable, so a caption that fitted at 420px wide does not at 360 —
     measuring once on mount would leave the "more" button behind, either
     missing or stuck. */
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = showCaptionToggle(el.scrollHeight, el.clientHeight);
    setClipped((prev) => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    if (open) return undefined;
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [open, src, measure]);

  if (!src.trim()) return null;

  return (
    <div className="vb-reel-cap">
      <p ref={ref} className={`vb-reel-captext ${open ? 'open' : ''}`}>
        {segments.map((seg, i) => (seg.tag ? (
          /* 🟢 A TAG IS A LINK WHERE IT WAS WRITTEN — see orphanHashtags for
             the remainder that genuinely needs a chip of its own. */
          <button
            key={`${seg.tag}-${i}`}
            type="button"
            className="vb-reel-tagword"
            onClick={(e) => { e.stopPropagation(); if (onTag) onTag(seg.tag); }}
          >
            {seg.text}
          </button>
        ) : (
          <span key={`s-${i}`}>{seg.text}</span>
        )))}
      </p>
      {(open || clipped) && (
        <button
          type="button"
          className="vb-reel-more"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'less' : 'more'}
        </button>
      )}
    </div>
  );
}

/* ── the hashtag strip ───────────────────────────────────────────────────── */

export function ReelTags({ tags, onTag }) {
  if (!tags || !tags.length) return null;
  return (
    <div className="vb-reel-tags">
      {tags.map((tag) => (
        <button
          key={tag}
          type="button"
          className="vb-reel-tag"
          onClick={(e) => { e.stopPropagation(); if (onTag) onTag(tag); }}
        >
          {`#${tag}`}
        </button>
      ))}
    </div>
  );
}

/* ── the safety notice ───────────────────────────────────────────────────── */

/**
 * 🟥 ABOVE THE NAME, NOT UNDER THE CAPTION. Beneath two clamped lines it would
 *    be a warning behind a "more" button, which is not a warning. Amber,
 *    because that is what the rest of the world uses for "careful" — a viewer
 *    should not have to read it to know what kind of thing it is.
 */
export function SafetyNotice({ kind }) {
  const [open, setOpen] = useState(false);
  if (!kind || !SAFETY_TEXT[kind]) return null;
  const keys = safetyKeys(kind);

  if (!open) {
    return (
      <button
        type="button"
        className="vb-safety-pill"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={SAFETY_TITLE}
      >
        <span aria-hidden="true">!</span>
        {SAFETY_PILL}
      </button>
    );
  }

  return (
    <div className="vb-safety" role="note">
      <div className="vb-safety-head">
        <span className="vb-safety-mark" aria-hidden="true">!</span>
        {SAFETY_TITLE}
      </div>
      {/* keyed exactly as the phone keys it, so the two cannot drift apart */}
      <p>{SAFETY_TEXT[keys.body.replace('vibe_safety_', '')]}</p>
      <button
        type="button"
        className="vb-safety-close"
        onClick={(e) => { e.stopPropagation(); setOpen(false); }}
      >
        {SAFETY_CLOSE}
      </button>
    </div>
  );
}
